import { Injectable } from '@angular/core';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import {
  NavigationDropdown,
  NavigationItem,
  NavigationLink
} from './navigation-item.interface';
import { NavigationVisibilityService } from './navigation-visibility.service';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NavigationLoaderService {
  private readonly hiddenAppLabels = [
    'Help Center',
    'Calendar',
    'Social',
    'WYSIWYG Editor',
    'Scrumboard',
    'All-In-One Table'
  ];

  private readonly _items: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  constructor(
    private readonly layoutService: VexLayoutService,
    private readonly navigationVisibility: NavigationVisibilityService
  ) {
    this.navigationVisibility.hideUnneededPages$.subscribe((hideUnneededPages) =>
      this.loadNavigation(hideUnneededPages)
    );
  }

  loadNavigation(hideUnneededPages: boolean): void {
    const appsChildren: Array<NavigationLink | NavigationDropdown> = [
      {
        type: 'link',
        label: 'ניהול מודעות',
        route: '/apps/ads',
        icon: 'mat:campaign',
        badge: {
          value: 'NEW',
          bgClass: 'bg-primary-600',
          textClass: 'text-on-primary-600'
        }
      },
      {
        // SCRUM-324 — coupons admin (overview, list, create, campaigns, redemptions, audit, settings)
        type: 'link',
        label: 'ניהול קופונים',
        route: '/apps/coupons',
        icon: 'mat:sell',
        routerLinkActiveOptions: { exact: false }
      },
      {
        type: 'link',
        label: 'הזמנות מרקט',
        route: '/apps/orders',
        icon: 'mat:shopping_cart'
      },
      {
        type: 'link',
        label: 'דוח תשלומי לקוחות',
        route: '/apps/payments-report',
        icon: 'mat:payments'
      },
      {
        type: 'link',
        label: 'ניהול תשלומים (פיננסי)',
        route: '/apps/payments-report/finance',
        icon: 'mat:account_balance'
      },
      {
        type: 'link',
        label: 'ניהול באנרים ופרסומות',
        route: '/ads-manager',
        icon: 'mat:ad_units'
      },
      {
        // SCRUM-196 — Messaging Center (SMS engine / InforU)
        type: 'dropdown',
        label: 'מרכז הודעות',
        icon: 'mat:sms',
        children: [
          { type: 'link', label: 'סקירה', route: '/apps/messaging', routerLinkActiveOptions: { exact: true } },
          { type: 'link', label: 'שליחה וקמפיינים', route: '/apps/messaging/send' },
          { type: 'link', label: 'יומן הודעות', route: '/apps/messaging/log' },
          { type: 'link', label: 'תבניות', route: '/apps/messaging/templates' },
          { type: 'link', label: 'נכנסות והסרות', route: '/apps/messaging/incoming' },
          { type: 'link', label: 'הגדרות SMS', route: '/apps/messaging/settings' }
        ]
      },
      {
        // Also listed under Tables; surfaced here because it's edited often
        // and was easy to miss nested in that tree.
        type: 'link',
        label: 'ניהול אבזור',
        route: '/tables/armature-list',
        icon: 'mat:build'
      },
      {
        type: 'dropdown',
        label: 'פרסום חברתי',
        icon: 'mat:share',
        children: [
          {
            type: 'link',
            label: 'שתף מודעות',
            route: '/apps/social-publisher/ads',
            icon: 'mat:campaign'
          },
          {
            type: 'link',
            label: 'כל הפוסטים',
            route: '/apps/social-publisher/posts',
            icon: 'mat:list'
          },
          {
            type: 'link',
            label: 'אנליטיקס',
            route: '/apps/social-publisher/overview',
            icon: 'mat:bar_chart'
          }
        ]
      },
      {
        type: 'dropdown',
        label: 'שילוט חוצות',
        icon: 'mat:tv',
        children: [
          {
            type: 'link',
            label: 'מסכים',
            route: '/apps/billboard/screens',
            icon: 'mat:tv'
          },
          {
            type: 'link',
            label: 'ציר זמן',
            route: '/apps/billboard/timeline',
            icon: 'mat:calendar_today'
          },
          {
            type: 'link',
            label: 'אישור הזמנות',
            route: '/apps/billboard/approvals',
            icon: 'mat:fact_check'
          },
          {
            type: 'link',
            label: 'דוח רווחיות',
            route: '/apps/billboard/profit-report',
            icon: 'mat:bar_chart'
          }
        ]
      },
      {
        type: 'dropdown',
        label: 'Design Studio',
        icon: 'mat:auto_awesome',
        children: [
          {
            type: 'link',
            label: 'סטודיו',
            route: '/apps/design-studio',
            icon: 'mat:auto_fix_high'
          },
          {
            type: 'link',
            label: 'מיתוג',
            route: '/apps/design-studio/brand',
            icon: 'mat:palette'
          },
          {
            type: 'link',
            label: 'רקעים',
            route: '/apps/design-studio/backgrounds',
            icon: 'mat:wallpaper'
          },
          {
            type: 'link',
            label: 'ספריית נכסים',
            route: '/apps/design-studio/library',
            icon: 'mat:photo_library'
          }
        ]
      },
      {
        type: 'link',
        label: 'All-In-One Table',
        route: '/apps/aio-table',
        icon: 'mat:assignment'
      },
      {
        type: 'dropdown',
        label: 'Help Center',
        icon: 'mat:contact_support',
        children: [
          {
            type: 'link',
            label: 'Getting Started',
            route: '/apps/help-center/getting-started'
          },
          {
            type: 'link',
            label: 'Pricing & Plans',
            route: '/apps/help-center/pricing'
          },
          {
            type: 'link',
            label: 'FAQ',
            route: '/apps/help-center/faq'
          },
          {
            type: 'link',
            label: 'Guides',
            route: '/apps/help-center/guides'
          }
        ]
      },
      {
        type: 'link',
        label: 'Calendar',
        route: '/apps/calendar',
        icon: 'mat:date_range',
        badge: {
          value: '12',
          bgClass: 'bg-purple-600',
          textClass: 'text-white'
        }
      },
      {
        type: 'link',
        label: 'צ\'אט',
        route: '/apps/chat',
        icon: 'mat:chat',
        badge: {
          value: '16',
          bgClass: 'bg-cyan-600',
          textClass: 'text-white'
        }
      },
      {
        type: 'link',
        label: 'תיבת דואר',
        route: '/apps/mail',
        icon: 'mat:mail'
      },
      {
        type: 'dropdown',
        label: 'Social',
        icon: 'mat:person_outline',
        children: [
          {
            type: 'link',
            label: 'Profile',
            route: '/apps/social',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Timeline',
            route: '/apps/social/timeline'
          }
        ]
      },
      {
        type: 'link',
        label: 'WYSIWYG Editor',
        route: '/apps/editor',
        icon: 'mat:chrome_reader_mode'
      },
      {
        type: 'dropdown',
        label: 'sale4all.label.alert.system',
        icon: 'mat:mail',
        children: [
          {
            type: 'link',
            label: 'sale4all.label.alert.system.table.publish.form',
            route: '/apps/alert/table'
          },
          {
            type: 'link',
            label: 'sale4all.label.alert.system.form.messages',
            route: '/apps/alert/form-messages'
          }
        ]
      },
      {
        // SCRUM-259 — business accounts (VIP): license review + settings
        type: 'link',
        label: 'חשבונות עסקיים (VIP)',
        route: '/apps/business-accounts',
        icon: 'mat:storefront'
      },
      {
        // In-site inquiries → WhatsApp relay
        type: 'link',
        label: 'פניות ווואטסאפ',
        route: '/apps/whatsapp-inquiries',
        icon: 'mat:forum'
      },
      {
        // SEC-08 (SCRUM-353) — privacy requests (access / correction / deletion), 30-day deadline
        type: 'link',
        label: 'פניות פרטיות',
        route: '/apps/privacy-requests',
        icon: 'mat:privacy_tip'
      },
      {
        type: 'dropdown',
        label: 'sale4all.label.users',
        icon: 'mat:contacts',
        children: [
          {
            type: 'link',
            label: 'sale4all.label.users.table',
            route: '/apps/contacts/table'
          }
        ]
      },
      {
        type: 'link',
        label: 'Scrumboard',
        route: '/apps/scrumboard',
        icon: 'mat:assessment',
        badge: {
          value: 'NEW',
          bgClass: 'bg-primary-600',
          textClass: 'text-on-primary-600'
        }
      }
    ];

    const pagesSection: NavigationItem = {
      type: 'subheading',
      label: 'Pages',
      children: [
        {
          type: 'dropdown',
          label: 'Authentication',
          icon: 'mat:lock',
          children: [
            {
              type: 'link',
              label: 'Login',
              route: '/login'
            },
            {
              type: 'link',
              label: 'Register',
              route: '/register'
            },
            {
              type: 'link',
              label: 'Forgot Password',
              route: '/forgot-password'
            }
          ]
        },
        {
          type: 'link',
          label: 'Coming Soon',
          icon: 'mat:watch_later',
          route: '/coming-soon'
        },
        {
          type: 'dropdown',
          label: 'Errors',
          icon: 'mat:error',
          badge: {
            value: '4',
            bgClass: 'bg-green-600',
            textClass: 'text-white'
          },
          children: [
            {
              type: 'link',
              label: '404',
              route: '/pages/error-404'
            },
            {
              type: 'link',
              label: '500',
              route: '/pages/error-500'
            }
          ]
        },
        {
          type: 'link',
          label: 'Pricing',
          icon: 'mat:attach_money',
          route: '/pages/pricing'
        },
        {
          type: 'link',
          label: 'Invoice',
          icon: 'mat:receipt',
          route: '/pages/invoice'
        },
        {
          type: 'link',
          label: 'FAQ',
          icon: 'mat:help',
          route: '/pages/faq'
        },
        {
          type: 'link',
          label: 'Guides',
          icon: 'mat:book',
          route: '/pages/guides',
          badge: {
            value: '18',
            bgClass: 'bg-teal-600',
            textClass: 'text-white'
          }
        }
      ]
    };

    const uiElementsSection: NavigationItem = {
      type: 'subheading',
      label: 'UI Elements',
      children: [
        {
          type: 'dropdown',
          label: 'Components',
          icon: 'mat:bubble_chart',
          children: [
            {
              type: 'link',
              label: 'Overview',
              route: '/ui/components/overview'
            },
            {
              type: 'link',
              label: 'Autocomplete',
              route: '/ui/components/autocomplete'
            },
            {
              type: 'link',
              label: 'Buttons',
              route: '/ui/components/buttons'
            },
            {
              type: 'link',
              label: 'Button Group',
              route: '/ui/components/button-group'
            },
            {
              type: 'link',
              label: 'Cards',
              route: '/ui/components/cards'
            },
            {
              type: 'link',
              label: 'Checkbox',
              route: '/ui/components/checkbox'
            },
            {
              type: 'link',
              label: 'Dialogs',
              route: '/ui/components/dialogs'
            },
            {
              type: 'link',
              label: 'Grid List',
              route: '/ui/components/grid-list'
            },
            {
              type: 'link',
              label: 'Input',
              route: '/ui/components/input'
            },
            {
              type: 'link',
              label: 'Lists',
              route: '/ui/components/lists'
            },
            {
              type: 'link',
              label: 'Menu',
              route: '/ui/components/menu'
            },
            {
              type: 'link',
              label: 'Progress',
              route: '/ui/components/progress'
            },
            {
              type: 'link',
              label: 'Progress Spinner',
              route: '/ui/components/progress-spinner'
            },
            {
              type: 'link',
              label: 'Radio',
              route: '/ui/components/radio'
            },
            {
              type: 'link',
              label: 'Slide Toggle',
              route: '/ui/components/slide-toggle'
            },
            {
              type: 'link',
              label: 'Slider',
              route: '/ui/components/slider'
            },
            {
              type: 'link',
              label: 'Snack Bar',
              route: '/ui/components/snack-bar'
            },
            {
              type: 'link',
              label: 'Tooltip',
              route: '/ui/components/tooltip'
            }
          ]
        },
        {
          type: 'dropdown',
          label: 'Forms',
          icon: 'mat:format_color_text',
          children: [
            {
              type: 'link',
              label: 'Form Elements',
              route: '/ui/forms/form-elements'
            },
            {
              type: 'link',
              label: 'Form Wizard',
              route: '/ui/forms/form-wizard'
            }
          ]
        },
        {
          type: 'dropdown',
          label: 'Icons',
          icon: 'mat:star',
          children: [
            {
              type: 'link',
              label: 'Material Icons',
              route: '/ui/icons/ic'
            },
            {
              type: 'link',
              label: 'FontAwesome Icons',
              route: '/ui/icons/fa'
            }
          ]
        },
        {
          type: 'dropdown',
          label: 'Page Layouts',
          icon: 'mat:view_compact',
          children: [
            {
              type: 'dropdown',
              label: 'Card',
              children: [
                {
                  type: 'link',
                  label: 'Default',
                  route: '/ui/page-layouts/card',
                  routerLinkActiveOptions: { exact: true }
                },
                {
                  type: 'link',
                  label: 'Tabbed',
                  route: '/ui/page-layouts/card/tabbed'
                },
                {
                  type: 'link',
                  label: 'Large Header',
                  route: '/ui/page-layouts/card/large-header',
                  routerLinkActiveOptions: { exact: true }
                },
                {
                  type: 'link',
                  label: 'Tabbed & Large Header',
                  route: '/ui/page-layouts/card/large-header/tabbed'
                }
              ]
            },
            {
              type: 'dropdown',
              label: 'Simple',
              children: [
                {
                  type: 'link',
                  label: 'Default',
                  route: '/ui/page-layouts/simple',
                  routerLinkActiveOptions: { exact: true }
                },
                {
                  type: 'link',
                  label: 'Tabbed',
                  route: '/ui/page-layouts/simple/tabbed'
                },
                {
                  type: 'link',
                  label: 'Large Header',
                  route: '/ui/page-layouts/simple/large-header',
                  routerLinkActiveOptions: { exact: true }
                },
                {
                  type: 'link',
                  label: 'Tabbed & Large Header',
                  route: '/ui/page-layouts/simple/large-header/tabbed'
                }
              ]
            },
            {
              type: 'link',
              label: 'Blank',
              icon: 'mat:picture_in_picture',
              route: '/ui/page-layouts/blank'
            }
          ]
        }
      ]
    };

    const documentationSection: NavigationItem = {
      type: 'subheading',
      label: 'Documentation',
      children: [
        {
          type: 'link',
          label: 'Changelog',
          route: '/documentation/changelog',
          icon: 'mat:update'
        },
        {
          type: 'dropdown',
          label: 'Getting Started',
          icon: 'mat:book',
          children: [
            {
              type: 'link',
              label: 'Introduction',
              route: '/documentation/introduction',
              fragment: 'introduction',
              routerLinkActiveOptions: { exact: true }
            },
            {
              type: 'link',
              label: 'Folder Structure',
              route: '/documentation/folder-structure',
              fragment: 'folder-structure',
              routerLinkActiveOptions: { exact: true }
            },
            {
              type: 'link',
              label: 'Installation',
              route: '/documentation/installation',
              fragment: 'installation',
              routerLinkActiveOptions: { exact: true }
            },
            {
              type: 'link',
              label: 'Development Server',
              route: '/documentation/start-development-server',
              fragment: 'start-development-server',
              routerLinkActiveOptions: { exact: true }
            },
            {
              type: 'link',
              label: 'Build for Production',
              route: '/documentation/build-for-production',
              fragment: 'build-for-production',
              routerLinkActiveOptions: { exact: true }
            }
          ]
        },
        {
          type: 'dropdown',
          label: 'Customization',
          icon: 'mat:book',
          children: [
            {
              type: 'link',
              label: 'Configuration',
              route: '/documentation/configuration',
              fragment: 'configuration',
              routerLinkActiveOptions: { exact: true }
            },
            {
              type: 'link',
              label: 'Changing Styling',
              route: '/documentation/changing-styling-and-css-variables',
              fragment: 'changing-styling-and-css-variables',
              routerLinkActiveOptions: { exact: true }
            },
            {
              type: 'link',
              label: 'Using Custom Colors',
              route:
                '/documentation/using-custom-colors-for-the-primarysecondarywarn-palettes',
              fragment:
                'using-custom-colors-for-the-primarysecondarywarn-palettes',
              routerLinkActiveOptions: { exact: true }
            },
            {
              type: 'link',
              label: 'Adding Menu Items',
              route: '/documentation/adding-menu-items',
              fragment: 'adding-menu-items',
              routerLinkActiveOptions: { exact: true }
            }
          ]
        },
        {
          type: 'link',
          label: 'Further Help',
          icon: 'mat:book',
          route: '/documentation/further-help',
          fragment: 'further-help',
          routerLinkActiveOptions: { exact: true }
        }
      ]
    };

    // Admin lookup tables. Its own top-level section rather than an entry under
    // Apps — these are reference-data tables, not applications. Grouped by
    // resource type, mirroring the old font-end sidebar tree (menu.ts Menu 30
    // and its 33/34/35 sub-groups).
    const tablesSection: NavigationItem = {
      type: 'subheading',
      label: 'טבלאות ניהול',
      children: [
        {
          type: 'link',
          label: 'sale4you.label.tables.armatureList',
          route: '/tables/armature-list',
          icon: 'mat:list_alt'
        },
        {
          type: 'link',
          label: 'sale4you.ads.routes',
          route: '/tables/ads-route',
          icon: 'mat:campaign'
        },
        {
          type: 'link',
          label: 'sale4you.label.tables.internalAds',
          route: '/tables/internal-ads',
          icon: 'mat:admin_panel_settings'
        },
        {
          type: 'link',
          label: 'sale4you.label.tables.processes',
          route: '/tables/processes',
          icon: 'mat:sync'
        },
        {
          type: 'dropdown',
          label: 'sale4you.label.cars',
          icon: 'mat:directions_car',
          children: [
            {
              type: 'link',
              label: 'sale4you.label.tables.carsColors',
              route: '/tables/cars/colors'
            },
            {
              type: 'link',
              label: 'sale4you.label.tables.monthList',
              route: '/tables/cars/month_list'
            },
            {
              type: 'link',
              label: 'sale4you.label.tables.yearsList',
              route: '/tables/cars/years_list'
            },
            {
              type: 'link',
              label: 'sale4you.label.owner_list',
              route: '/tables/cars/owner_list'
            },
            {
              type: 'link',
              label: 'sale4you.label.car_hand_list',
              route: '/tables/cars/car-hand-list'
            },
            {
              type: 'link',
              label: 'sale4you.label.tables.carMonthOnRoadList',
              route: '/tables/cars/month_on_road_list'
            },
            {
              type: 'link',
              label: 'sale4you.label.tables.carDgmList',
              route: '/tables/archive/car-dgm-list'
            },
            {
              type: 'link',
              label: 'sale4you.label.tables.carGovWltpList',
              route: '/tables/archive/cargovwltp'
            }
          ]
        },
        {
          type: 'dropdown',
          label: 'sale4you.label.sale2',
          icon: 'mat:shopping_bag',
          children: [
            {
              type: 'link',
              label: 'sale4you.label.tables.sale2SubCategoryList',
              route: '/tables/sale2/sale2-subcategory-list'
            },
            {
              type: 'link',
              label: 'sale4you.label.tables.sale2ThirdCategory',
              route: '/tables/sale2/sale2-third-category'
            },
            {
              type: 'link',
              label: 'sale4you.label.tables.sale2ProductStatusList',
              route: '/tables/sale2/product-status-list'
            }
          ]
        },
        {
          type: 'dropdown',
          label: 'sale4you.label.nadlans',
          icon: 'mat:home_work',
          children: [
            {
              type: 'link',
              label: 'sale4you.label.tables.nadlanStatusList',
              route: '/tables/nadlan/nadlan-status-nadlan-list'
            },
            {
              type: 'link',
              label: 'sale4you.label.tables.nadlanTypeList',
              route: '/tables/nadlan/nadlan-type-nadlan-list'
            },
            {
              type: 'link',
              label: 'sale4you.label.tables.nadlanRoomNumbersList',
              route: '/tables/nadlan/nadlan-room-numbers-list'
            },
            {
              type: 'link',
              label: 'sale4you.label.tables.nadlanParkingList',
              route: '/tables/nadlan/nadlan-parking-list'
            },
            {
              type: 'link',
              label: 'sale4you.label.tables.nadlanBalconyList',
              route: '/tables/nadlan/nadlan-balcony-list'
            },
            {
              type: 'link',
              label: 'sale4you.label.tables.neighborhoods',
              route: '/tables/nadlan/neighborhoods'
            }
          ]
        },
        {
          type: 'dropdown',
          label: 'sale4you.label.tables.general',
          icon: 'mat:tune',
          children: [
            {
              type: 'link',
              label: 'homepage.carousel-slider-admin',
              route: '/tables/homepage/carousel-slider-admin'
            },
            {
              // SCRUM-193
              type: 'link',
              label: 'sale4you.label.tables.appPopup',
              route: '/tables/homepage/app-entry-popup'
            }
          ]
        }
      ]
    };

    const items: NavigationItem[] = [
      {
        type: 'subheading',
        label: 'לוח בקרה',
        children: [
          {
            type: 'link',
            label: 'נתוח נתונים',
            route: '/',
            icon: 'mat:bar_chart',
            routerLinkActiveOptions: { exact: true }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'אפליקציות',
        children: hideUnneededPages
          ? appsChildren.filter(
              (item) => !this.hiddenAppLabels.includes(item.label)
            )
          : appsChildren
      },
      tablesSection,
      ...(hideUnneededPages ? [] : [pagesSection]),
      ...(hideUnneededPages ? [] : [uiElementsSection]),
      ...(hideUnneededPages ? [] : [documentationSection]),
      {
        type: 'subheading',
        label: 'התאמה אישית',
        children: []
      },
      {
        type: 'link',
        label: 'הגדרות תצוגה',
        route: () => this.layoutService.openConfigpanel(),
        icon: 'mat:settings'
      }
    ];

    this._items.next(items);
  }
}
